import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { backendEndPointToken } from '../app.config';
import { StateService } from '../shared/state.service';
import { Bundle, Condition, DiagnosticReport, Medication, MedicationRequest, Observation, ServiceRequest, AllergyIntolerance, Immunization } from 'fhir/r4';
import { UtilityService } from '../shared/utility.service';
import { BehaviorSubject, catchError, distinctUntilChanged, filter, ignoreElements, map, merge, Observable, of, race, shareReplay, switchMap, take, tap } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class CheckSheetService {

  private http = inject(HttpClient);
  private backendUrl = inject(backendEndPointToken);
  private state = inject(StateService);
  private utility = inject(UtilityService);

  private readonly patientSymptomsLoadingSubject = new BehaviorSubject<boolean>(false);
  readonly patientSymptomsLoading$ = this.patientSymptomsLoadingSubject.asObservable();

  private readonly vitalsLoadingSubject = new BehaviorSubject<boolean>(false);
  readonly vitalsLoading$ = this.vitalsLoadingSubject.asObservable();

  private readonly physicalExamLoadingSubject = new BehaviorSubject<boolean>(false);
  readonly physicalExamLoading$ = this.physicalExamLoadingSubject.asObservable();

  private readonly labRequestsLoadingSubject = new BehaviorSubject<boolean>(false);
  readonly labRequestsLoading$ = this.labRequestsLoadingSubject.asObservable();

  private readonly diagnosisLoadingSubject = new BehaviorSubject<boolean>(false);
  readonly diagnosisLoading$ = this.diagnosisLoadingSubject.asObservable();

  private readonly finalDiagnosticReportLoadingSubject = new BehaviorSubject<boolean>(false);
  readonly finalDiagnosticReportLoading$ = this.finalDiagnosticReportLoadingSubject.asObservable();

  private readonly finalDiagnosticReportsForEncounterLoadingSubject = new BehaviorSubject<boolean>(false);
  readonly finalDiagnosticReportsForEncounterLoading$ = this.finalDiagnosticReportsForEncounterLoadingSubject.asObservable();

  private readonly medicationRequestsLoadingSubject = new BehaviorSubject<boolean>(false);
  readonly medicationRequestsLoading$ = this.medicationRequestsLoadingSubject.asObservable();

  private readonly pastDiagnosisLoadingSubject = new BehaviorSubject<boolean>(false);
  readonly pastDiagnosisLoading$ = this.pastDiagnosisLoadingSubject.asObservable();

  private readonly immunizationHistoryLoadingSubject = new BehaviorSubject<boolean>(false);
  readonly immunizationHistoryLoading$ = this.immunizationHistoryLoadingSubject.asObservable();

  private patientSymptomsLive$?: Observable<Observation[]>;
  private vitalsLive$?: Observable<Observation[]>;
  private physicalExamLive$?: Observable<Observation[]>;
  private serviceRequestsForEncounterLive$?: Observable<ServiceRequest[]>;
  private labRequestsLive$?: Observable<ServiceRequest[]>;
  private otherRequestsLive$?: Observable<ServiceRequest[]>;
  private diagnosisLive$?: Observable<Condition[]>;
  private pastDiagnosisLive$?: Observable<Condition[]>;
  private immunizationHistoryLive$?: Observable<Immunization[]>;
  private finalDiagnosticReportsForEncounterLive$?: Observable<DiagnosticReport[]>;
  private medicationRequestsLive$?: Observable<MedicationRequest[]>;
  private medicationRequestsForEncounterLive$?: Observable<MedicationRequest[]>;
  private allergyIntolerancesLive$?: Observable<AllergyIntolerance[]>;

  private readonly allergyIntolerancesLoadingSubject = new BehaviorSubject<boolean>(false);
  readonly allergyIntolerancesLoading$ = this.allergyIntolerancesLoadingSubject.asObservable();

  private finalDiagnosticReportByServiceRequest = new Map<string, Observable<DiagnosticReport | null>>();

  constructor() {

  }

  /**
   * Lazy/live stream for “Patient Presented Symptoms & Social History”.
   *
   * - Designed for expansion panels: only starts loading when you subscribe.
   * - Uses UtilityService.threeTierQueryAndSyncToStateOnce$ to fetch once (best-effort).
   * - Emits continuously from StateService (so updates push automatically) until the subscriber unsubscribes.
   */
  connectPatientSymptoms$(subjectIdToPass: string, encounterIdToPass: string, sourceData?: Bundle<Observation> | Observation[] | null): Observable<Observation[]> {
    if (this.patientSymptomsLive$) return this.patientSymptomsLive$;

    const normalizedPatientIdFromArgs = this.normalizeIdFromReference('Patient', subjectIdToPass);
    const normalizedEncounterIdFromArgs = this.normalizeIdFromReference('Encounter', encounterIdToPass);

    const patientId$ = normalizedPatientIdFromArgs
      ? of(normalizedPatientIdFromArgs)
      : this.state.currentPatientId$.pipe(distinctUntilChanged());

    this.patientSymptomsLive$ = patientId$.pipe(
      distinctUntilChanged(),
      switchMap((patientId) => {
        if (!patientId) return of([] as Observation[]);

        const fromInput = this.extractObservations(sourceData);
        if (fromInput) {
          return of(this.filterSymptomObservations(fromInput, patientId, normalizedEncounterIdFromArgs));
        }

        const stateFiltered$ = this.loadPatientObservationResources().pipe(
          map((wrapped) => (wrapped ?? []).map((r) => r.actualResource).filter(Boolean) as Observation[]),
          map((observations) => this.filterSymptomObservations(observations, patientId, normalizedEncounterIdFromArgs))
        );

        // Kick off a best-effort fetch (results are persisted to state by UtilityService).
        this.patientSymptomsLoadingSubject.next(true);
        const load$ = this.utility.threeTierQueryAndSyncToStateOnce$<Observation>({
          resourceType: 'Observation',
          scope: 'patient',
          patientId,
          patientScopeSearchParam: 'subject',

          urlSearchObject: {
            subject: `Patient/${patientId}`,
            encounter: normalizedEncounterIdFromArgs ? `Encounter/${normalizedEncounterIdFromArgs}` : undefined,


          },
          // Local filtering happens in stateFiltered$; keep querySearchObject minimal to avoid server rejections.
          querySearchObject: [
            { key: 'subject.reference', value: [`Patient/${patientId}`, `${this.backendUrl}/Patient/${patientId}`] },
            { key: 'status', value: ['final', 'corrected'] },
            ...(normalizedEncounterIdFromArgs
              ? [{ key: 'encounter.reference', value: [`Encounter/${normalizedEncounterIdFromArgs}`, `${this.backendUrl}/Encounter/${normalizedEncounterIdFromArgs}`] }]
              : []),


          ],
          tier1FollowNextLinks: true,
          tier3TriggerWhenBelowExpectedMinOnTier1Failure: false,
        }).pipe(
          tap((observations) => {
            const count = observations?.length ?? 0;
            console.log(`Fetched ${count} symptom-related observations for patient ${patientId}.`);
          }),
          tap(() => this.patientSymptomsLoadingSubject.next(false)),
          catchError(() => {
            this.patientSymptomsLoadingSubject.next(false);
            return of([] as Observation[]);
          }),
          ignoreElements()
        );

        // Stream state; load$ runs side-effects that cause stateFiltered$ to re-emit.
        return merge(stateFiltered$, load$).pipe(
          tap((e) => console.log('Emitting patient symptoms update', e))
        );
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    return this.patientSymptomsLive$;
  }

  /**
   * Lazy/live stream for “Vitals” observations for a specific encounter.
   * Uses the same 3-tier pattern as symptoms (load once, then keep emitting from state).
   */
  connectPatientVitals$(subjectIdToPass: string, encounterIdToPass: string, sourceData?: Bundle<Observation> | Observation[] | null): Observable<Observation[]> {
    if (this.vitalsLive$) return this.vitalsLive$;

    const normalizedPatientIdFromArgs = this.normalizeIdFromReference('Patient', subjectIdToPass);
    const normalizedEncounterIdFromArgs = this.normalizeIdFromReference('Encounter', encounterIdToPass);

    const patientId$ = normalizedPatientIdFromArgs
      ? of(normalizedPatientIdFromArgs)
      : this.state.currentPatientId$.pipe(distinctUntilChanged());

    this.vitalsLive$ = patientId$.pipe(
      distinctUntilChanged(),
      switchMap((patientId) => {
        if (!patientId) return of([] as Observation[]);
        if (!normalizedEncounterIdFromArgs) return of([] as Observation[]);

        const fromInput = this.extractObservations(sourceData);
        if (fromInput) {
          return of(this.filterVitalObservations(fromInput, patientId, normalizedEncounterIdFromArgs));
        }

        const stateFiltered$ = this.loadPatientObservationResources().pipe(
          map((wrapped) => (wrapped ?? []).map((r) => r.actualResource).filter(Boolean) as Observation[]),
          map((observations) => this.filterVitalObservations(observations, patientId, normalizedEncounterIdFromArgs))
        );

        this.vitalsLoadingSubject.next(true);
        const load$ = this.utility.threeTierQueryAndSyncToStateOnce$<Observation>({
          resourceType: 'Observation',
          scope: 'patient',
          patientId,
          patientScopeSearchParam: 'subject',
          urlSearchObject: {
            subject: `Patient/${patientId}`,
            encounter: `Encounter/${normalizedEncounterIdFromArgs}`,
          },
          querySearchObject: [
            { key: 'subject.reference', value: [`Patient/${patientId}`, `${this.backendUrl}/Patient/${patientId}`] },
            { key: 'encounter.reference', value: [`Encounter/${normalizedEncounterIdFromArgs}`, `${this.backendUrl}/Encounter/${normalizedEncounterIdFromArgs}`] },
          ],
          tier1FollowNextLinks: true,
          tier3TriggerWhenBelowExpectedMinOnTier1Failure: false,
        }).pipe(
          tap(() => this.vitalsLoadingSubject.next(false)),
          catchError(() => {
            this.vitalsLoadingSubject.next(false);
            return of([] as Observation[]);
          }),
          ignoreElements()
        );

        return merge(stateFiltered$, load$);
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    return this.vitalsLive$;
  }

  /**
   * Lazy/live stream for “Physical Examination” findings for a specific encounter.
   * Uses the same 3-tier pattern as symptoms.
   */
  connectPhysicalExam$(subjectIdToPass: string, encounterIdToPass: string, sourceData?: Bundle<Observation> | Observation[] | null): Observable<Observation[]> {
    if (this.physicalExamLive$) return this.physicalExamLive$;

    const normalizedPatientIdFromArgs = this.normalizeIdFromReference('Patient', subjectIdToPass);
    const normalizedEncounterIdFromArgs = this.normalizeIdFromReference('Encounter', encounterIdToPass);

    const patientId$ = normalizedPatientIdFromArgs
      ? of(normalizedPatientIdFromArgs)
      : this.state.currentPatientId$.pipe(distinctUntilChanged());

    this.physicalExamLive$ = patientId$.pipe(
      distinctUntilChanged(),
      switchMap((patientId) => {
        if (!patientId) return of([] as Observation[]);
        if (!normalizedEncounterIdFromArgs) return of([] as Observation[]);

        const fromInput = this.extractObservations(sourceData);
        if (fromInput) {
          return of(this.filterPhysicalExamObservations(fromInput, patientId, normalizedEncounterIdFromArgs));
        }

        const stateFiltered$ = this.loadPatientObservationResources().pipe(
          map((wrapped) => (wrapped ?? []).map((r) => r.actualResource).filter(Boolean) as Observation[]),
          map((observations) => this.filterPhysicalExamObservations(observations, patientId, normalizedEncounterIdFromArgs))
        );

        this.physicalExamLoadingSubject.next(true);
        const load$ = this.utility.threeTierQueryAndSyncToStateOnce$<Observation>({
          resourceType: 'Observation',
          scope: 'patient',
          patientId,
          patientScopeSearchParam: 'subject',
          urlSearchObject: {
            subject: `Patient/${patientId}`,
            encounter: `Encounter/${normalizedEncounterIdFromArgs}`,
          },
          querySearchObject: [
            { key: 'subject.reference', value: [`Patient/${patientId}`, `${this.backendUrl}/Patient/${patientId}`] },
            { key: 'status', value: ['final', 'corrected'] },
            { key: 'encounter.reference', value: [`Encounter/${normalizedEncounterIdFromArgs}`, `${this.backendUrl}/Encounter/${normalizedEncounterIdFromArgs}`] },
          ],
          tier1FollowNextLinks: true,
          tier3TriggerWhenBelowExpectedMinOnTier1Failure: false,
        }).pipe(
          tap(() => this.physicalExamLoadingSubject.next(false)),
          catchError(() => {
            this.physicalExamLoadingSubject.next(false);
            return of([] as Observation[]);
          }),
          ignoreElements()
        );

        return merge(stateFiltered$, load$);
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    return this.physicalExamLive$;
  }

  /**
   * Lazy/live stream for lab ServiceRequests for a specific encounter.
   * Uses the same 3-tier pattern as symptoms/vitals/exam.
   */
  connectLabServiceRequests$(subjectIdToPass: string, encounterIdToPass: string, sourceData?: Bundle<ServiceRequest> | ServiceRequest[] | null): Observable<ServiceRequest[]> {
    if (this.labRequestsLive$) return this.labRequestsLive$;

    this.labRequestsLive$ = this.connectServiceRequestsForEncounter$(subjectIdToPass, encounterIdToPass, sourceData).pipe(
      map((requests) => (requests ?? []).filter((sr) => this.isLabServiceRequest(sr))),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    return this.labRequestsLive$;
  }

  /**
   * Lazy/live stream for non-lab ServiceRequests for a specific encounter.
   */
  connectOtherServiceRequests$(subjectIdToPass: string, encounterIdToPass: string, sourceData?: Bundle<ServiceRequest> | ServiceRequest[] | null): Observable<ServiceRequest[]> {
    if (this.otherRequestsLive$) return this.otherRequestsLive$;

    this.otherRequestsLive$ = this.connectServiceRequestsForEncounter$(subjectIdToPass, encounterIdToPass, sourceData).pipe(
      map((requests) => (requests ?? []).filter((sr) => !this.isLabServiceRequest(sr))),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    return this.otherRequestsLive$;
  }

  private connectServiceRequestsForEncounter$(subjectIdToPass: string, encounterIdToPass: string, sourceData?: Bundle<ServiceRequest> | ServiceRequest[] | null): Observable<ServiceRequest[]> {
    if (this.serviceRequestsForEncounterLive$) return this.serviceRequestsForEncounterLive$;

    const normalizedPatientIdFromArgs = this.normalizeIdFromReference('Patient', subjectIdToPass);
    const normalizedEncounterIdFromArgs = this.normalizeIdFromReference('Encounter', encounterIdToPass);

    const patientId$ = normalizedPatientIdFromArgs
      ? of(normalizedPatientIdFromArgs)
      : this.state.currentPatientId$.pipe(distinctUntilChanged());

    this.serviceRequestsForEncounterLive$ = patientId$.pipe(
      distinctUntilChanged(),
      switchMap((patientId) => {
        if (!patientId) return of([] as ServiceRequest[]);
        if (!normalizedEncounterIdFromArgs) return of([] as ServiceRequest[]);

        const fromInput = this.extractServiceRequests(sourceData);
        if (fromInput) {
          return of(this.filterServiceRequestsForEncounter(fromInput, patientId, normalizedEncounterIdFromArgs));
        }

        const stateFiltered$ = this.state.PatientResources.serviceRequests.pipe(
          map((wrapped) => (wrapped ?? []).map((r) => r.actualResource).filter(Boolean) as any[]),
          map((resources) => resources.filter((r) => (r as any)?.resourceType === 'ServiceRequest') as ServiceRequest[]),
          map((requests) => this.filterServiceRequestsForEncounter(requests, patientId, normalizedEncounterIdFromArgs))
        );

        this.labRequestsLoadingSubject.next(true);
        const load$ = this.utility.threeTierQueryAndSyncToStateOnce$<ServiceRequest>({
          resourceType: 'ServiceRequest',
          scope: 'patient',
          patientId,
          patientScopeSearchParam: 'subject',
          urlSearchObject: {
            subject: `Patient/${patientId}`,
            encounter: `Encounter/${normalizedEncounterIdFromArgs}`,
          },
          querySearchObject: [
            { key: 'subject.reference', value: [`Patient/${patientId}`, `${this.backendUrl}/Patient/${patientId}`] },
            { key: 'status', value: ['active', 'completed', 'revoked'] },
            { key: 'encounter.reference', value: [`Encounter/${normalizedEncounterIdFromArgs}`, `${this.backendUrl}/Encounter/${normalizedEncounterIdFromArgs}`] },
          ],
          tier1FollowNextLinks: true,
          tier3TriggerWhenBelowExpectedMinOnTier1Failure: false,
        }).pipe(
          tap(() => this.labRequestsLoadingSubject.next(false)),
          catchError(() => {
            this.labRequestsLoadingSubject.next(false);
            return of([] as ServiceRequest[]);
          }),
          ignoreElements()
        );

        return merge(stateFiltered$, load$);
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    return this.serviceRequestsForEncounterLive$;
  }

  private isLabServiceRequest(sr: ServiceRequest): boolean {
    const categories = (sr as any)?.category as any[] | undefined;
    const catList = Array.isArray(categories) ? categories : (categories ? [categories] : []);

    for (const cat of catList) {
      const text = (cat?.text ?? '').toString().toLowerCase();
      if (text.includes('lab') || text.includes('laboratory')) return true;

      const codes = (cat?.coding ?? []).map((c: any) => (c?.code ?? '').toString().toLowerCase());
      if (codes.includes('laboratory') || codes.includes('lab')) return true;

      if (this.utility.searchCodeableConceptByDisplayOrText(cat as any, 'laboratory')) return true;
      if (this.utility.searchCodeableConceptByDisplayOrText(cat as any, 'lab')) return true;
    }
    return false;
  }

  /**
   * Lazy/live stream for Diagnosis (FHIR Condition) for a specific encounter.
   * - Starts only when subscribed (e.g., expansion panel opened)
   * - Kicks off a best-effort three-tier fetch
   * - Continuously emits from StateService until unsubscribed
   */
  connectDiagnosisConditions$(subjectIdToPass: string, encounterIdToPass: string, sourceData?: Bundle<Condition> | Condition[] | null): Observable<Condition[]> {
    if (this.diagnosisLive$) return this.diagnosisLive$;

    const normalizedPatientIdFromArgs = this.normalizeIdFromReference('Patient', subjectIdToPass);
    const normalizedEncounterIdFromArgs = this.normalizeIdFromReference('Encounter', encounterIdToPass);

    const patientId$ = normalizedPatientIdFromArgs
      ? of(normalizedPatientIdFromArgs)
      : this.state.currentPatientId$.pipe(distinctUntilChanged());

    this.diagnosisLive$ = patientId$.pipe(
      distinctUntilChanged(),
      switchMap((patientId) => {
        if (!patientId) return of([] as Condition[]);
        if (!normalizedEncounterIdFromArgs) return of([] as Condition[]);

        const fromInput = this.extractConditions(sourceData);
        if (fromInput) {
          return of(this.filterDiagnosisConditionsForEncounter(fromInput, patientId, normalizedEncounterIdFromArgs));
        }

        const stateFiltered$ = this.state.PatientResources.condition.pipe(
          map((wrapped) => (wrapped ?? []).map((r) => r.actualResource).filter(Boolean) as Condition[]),
          map((conditions) => this.filterDiagnosisConditionsForEncounter(conditions, patientId, normalizedEncounterIdFromArgs))
        );

        this.diagnosisLoadingSubject.next(true);
        const load$ = this.utility.threeTierQueryAndSyncToStateOnce$<Condition>({
          resourceType: 'Condition',
          scope: 'patient',
          patientId,
          patientScopeSearchParam: 'subject',
          urlSearchObject: {
            subject: `Patient/${patientId}`,
            encounter: `Encounter/${normalizedEncounterIdFromArgs}`
          } as any,
          querySearchObject: [
            { key: 'subject.reference', value: [`Patient/${patientId}`, `${this.backendUrl}/Patient/${patientId}`] },
            { key: 'encounter.reference', value: [`Encounter/${normalizedEncounterIdFromArgs}`, `${this.backendUrl}/Encounter/${normalizedEncounterIdFromArgs}`] }
          ],
          tier1FollowNextLinks: true,
          tier3TriggerWhenBelowExpectedMinOnTier1Failure: false,
        }).pipe(
          tap(() => this.diagnosisLoadingSubject.next(false)),
          catchError(() => {
            this.diagnosisLoadingSubject.next(false);
            return of([] as Condition[]);
          }),
          ignoreElements()
        );

        return merge(stateFiltered$, load$);
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    return this.diagnosisLive$;
  }

  /**
   * Lazy/live stream of FINAL DiagnosticReports for a patient+encounter.
   * Used to quickly mark which ServiceRequests already have a final report.
   */
  connectFinalDiagnosticReportsForEncounter$(subjectIdToPass: string, encounterIdToPass: string, sourceData?: Bundle<DiagnosticReport> | DiagnosticReport[] | null): Observable<DiagnosticReport[]> {
    if (this.finalDiagnosticReportsForEncounterLive$) return this.finalDiagnosticReportsForEncounterLive$;

    const normalizedPatientIdFromArgs = this.normalizeIdFromReference('Patient', subjectIdToPass);
    const normalizedEncounterIdFromArgs = this.normalizeIdFromReference('Encounter', encounterIdToPass);

    const patientId$ = normalizedPatientIdFromArgs
      ? of(normalizedPatientIdFromArgs)
      : this.state.currentPatientId$.pipe(distinctUntilChanged());

    this.finalDiagnosticReportsForEncounterLive$ = patientId$.pipe(
      distinctUntilChanged(),
      switchMap((patientId) => {
        if (!patientId) return of([] as DiagnosticReport[]);
        if (!normalizedEncounterIdFromArgs) return of([] as DiagnosticReport[]);

        const fromInput = this.extractDiagnosticReports(sourceData);
        if (fromInput) {
          return of(this.filterFinalDiagnosticReportsForEncounter(fromInput, patientId, normalizedEncounterIdFromArgs));
        }

        const stateFiltered$ = this.state.PatientResources.diagnosticReports.pipe(
          map((wrapped) => (wrapped ?? []).map((r) => r.actualResource).filter(Boolean) as DiagnosticReport[]),
          map((reports) => this.filterFinalDiagnosticReportsForEncounter(reports, patientId, normalizedEncounterIdFromArgs))
        );

        this.finalDiagnosticReportsForEncounterLoadingSubject.next(true);
        const load$ = this.utility.threeTierQueryAndSyncToStateOnce$<DiagnosticReport>({
          resourceType: 'DiagnosticReport',
          scope: 'patient',
          patientId,
          patientScopeSearchParam: 'subject',
          urlSearchObject: {
            subject: `Patient/${patientId}`,
            // encounter: `Encounter/${normalizedEncounterIdFromArgs}`,
            status: 'final',
          } as any,
          querySearchObject: [
            { key: 'subject.reference', value: [`Patient/${patientId}`, patientId, `${this.backendUrl}/Patient/${patientId}`] },
            { key: 'status', value: ['final'] },
            //basedon
            // { key: 'basedon.reference', value: [serviceRequestId] },
            // { key: 'encounter.reference', value: [`Encounter/${normalizedEncounterIdFromArgs}`, normalizedEncounterIdFromArgs, `${this.backendUrl}/Encounter/${normalizedEncounterIdFromArgs}`] },
          ],
          tier1FollowNextLinks: true,
          tier3TriggerWhenBelowExpectedMinOnTier1Failure: false,
        }).pipe(
          tap((e) => {
// $#$            // /* alert('Fetched final diagnostic reports for encounter'+ JSON.stringify(e) */);
            this.finalDiagnosticReportsForEncounterLoadingSubject.next(false);
          }),
          catchError(() => {
            this.finalDiagnosticReportsForEncounterLoadingSubject.next(false);
            return of([] as DiagnosticReport[]);
          }),
          // ignoreElements()
        );

        // return merge(stateFiltered$, load$);
        return load$
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    return this.finalDiagnosticReportsForEncounterLive$;
  }

  /**
   * On-demand lookup: find the *final* DiagnosticReport for a given ServiceRequest.
   * Loads via three-tier query, but also checks StateService continuously.
   */
  findFinalDiagnosticReportForServiceRequest$(subjectIdToPass: string, encounterIdToPass: string, serviceRequestId: string): Observable<DiagnosticReport | null> {
    const srId = (serviceRequestId ?? '').toString().trim();
    if (!srId) return of(null);

    if (this.finalDiagnosticReportByServiceRequest.has(srId)) {
      return this.finalDiagnosticReportByServiceRequest.get(srId)!;
    }

    const normalizedPatientIdFromArgs = this.normalizeIdFromReference('Patient', subjectIdToPass);
    const normalizedEncounterIdFromArgs = this.normalizeIdFromReference('Encounter', encounterIdToPass);

    const patientId$ = normalizedPatientIdFromArgs
      ? of(normalizedPatientIdFromArgs)
      : this.state.currentPatientId$.pipe(distinctUntilChanged());

    const stream$ = patientId$.pipe(
      switchMap((patientId) => {
        if (!patientId) return of(null);

        const stateMatch$ = this.state.PatientResources.diagnosticReports.pipe(
          map((wrapped) => (wrapped ?? []).map((r) => r.actualResource).filter(Boolean) as DiagnosticReport[]),
          map((reports) => this.pickFinalDiagnosticReportForServiceRequest(reports, srId))
        );

        const stateHit$ = stateMatch$.pipe(
          filter((r): r is DiagnosticReport => !!r),
          take(1)
        );

        this.finalDiagnosticReportLoadingSubject.next(true);
        const load$ = this.utility.threeTierQueryAndSyncToStateOnce$<DiagnosticReport>({
          resourceType: 'DiagnosticReport',
          scope: 'patient',
          patientId,
          patientScopeSearchParam: 'subject',
          urlSearchObject: {
            subject: `Patient/${patientId}`,
            ...(normalizedEncounterIdFromArgs ? { encounter: `Encounter/${normalizedEncounterIdFromArgs}` } : {}),
            'based-on': `ServiceRequest/${srId}`,
            status: 'final',
          } as any,
          querySearchObject: [
            { key: 'subject.reference', value: [`Patient/${patientId}`, `${this.backendUrl}/Patient/${patientId}`] },
            ...(normalizedEncounterIdFromArgs
              ? [{ key: 'encounter.reference', value: [`Encounter/${normalizedEncounterIdFromArgs}`, `${this.backendUrl}/Encounter/${normalizedEncounterIdFromArgs}`] }]
              : []),
            { key: 'basedOn.reference', value: [`ServiceRequest/${srId}`, `${this.backendUrl}/ServiceRequest/${srId}`] },
          ],
          tier1FollowNextLinks: true,
          tier3TriggerWhenBelowExpectedMinOnTier1Failure: false,
        }).pipe(
          tap(() => this.finalDiagnosticReportLoadingSubject.next(false)),
          catchError(() => {
            this.finalDiagnosticReportLoadingSubject.next(false);
            return of([] as DiagnosticReport[]);
          }),
          map((reports) => this.pickFinalDiagnosticReportForServiceRequest(reports ?? [], srId))
        );

        // Prefer a state hit if already present; otherwise return the fetch result (may be null).
        return race(stateHit$, load$.pipe(take(1)));
      }),
      catchError(() => {
        this.finalDiagnosticReportLoadingSubject.next(false);
        return of(null);
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this.finalDiagnosticReportByServiceRequest.set(srId, stream$);
    return stream$;
  }

  /**
   * Lazy/live stream for MedicationRequests for a specific encounter.
   */
  connectMedicationRequests$(subjectIdToPass: string, encounterIdToPass: string, sourceData?: Bundle<MedicationRequest> | MedicationRequest[] | null): Observable<MedicationRequest[]> {
    if (this.medicationRequestsLive$) return this.medicationRequestsLive$;

    this.medicationRequestsLive$ = this.connectMedicationRequestsForEncounter$(subjectIdToPass, encounterIdToPass, sourceData).pipe(
      shareReplay({ bufferSize: 1, refCount: true })
    );

    return this.medicationRequestsLive$;
  }

  private connectMedicationRequestsForEncounter$(subjectIdToPass: string, encounterIdToPass: string, sourceData?: Bundle<MedicationRequest> | MedicationRequest[] | null): Observable<MedicationRequest[]> {
    if (this.medicationRequestsForEncounterLive$) return this.medicationRequestsForEncounterLive$;

    const normalizedPatientIdFromArgs = this.normalizeIdFromReference('Patient', subjectIdToPass);
    const normalizedEncounterIdFromArgs = this.normalizeIdFromReference('Encounter', encounterIdToPass);

    const patientId$ = normalizedPatientIdFromArgs
      ? of(normalizedPatientIdFromArgs)
      : this.state.currentPatientId$.pipe(distinctUntilChanged());

    this.medicationRequestsForEncounterLive$ = patientId$.pipe(
      distinctUntilChanged(),
      switchMap((patientId) => {
        if (!patientId) return of([] as MedicationRequest[]);
        if (!normalizedEncounterIdFromArgs) return of([] as MedicationRequest[]);

        const fromInput = this.extractMedicationRequests(sourceData);
        if (fromInput) {
          return of(this.filterMedicationRequestsForEncounter(fromInput, patientId, normalizedEncounterIdFromArgs));
        }

        const stateFiltered$ = this.state.PatientResources.medicationRequests.pipe(
          map((wrapped) => (wrapped ?? []).map((r) => r.actualResource).filter(Boolean) as MedicationRequest[]),
          map((requests) => this.filterMedicationRequestsForEncounter(requests, patientId, normalizedEncounterIdFromArgs))
        );

        this.medicationRequestsLoadingSubject.next(true);
        const load$ = this.utility.threeTierQueryAndSyncToStateOnce$<MedicationRequest>({
          resourceType: 'MedicationRequest',
          scope: 'patient',
          patientId,
          patientScopeSearchParam: 'subject',
          urlSearchObject: {
            subject: `Patient/${patientId}`,
            encounter: `Encounter/${normalizedEncounterIdFromArgs}`,
          },
          querySearchObject: [
            { key: 'subject.reference', value: [`Patient/${patientId}`, `${this.backendUrl}/Patient/${patientId}`] },
            { key: 'status', value: ['active', 'completed', 'stopped', 'on-hold'] },
            { key: 'encounter.reference', value: [`Encounter/${normalizedEncounterIdFromArgs}`, `${this.backendUrl}/Encounter/${normalizedEncounterIdFromArgs}`] },
          ],
          tier1FollowNextLinks: true,
          tier3TriggerWhenBelowExpectedMinOnTier1Failure: false,
        }).pipe(
          tap(() => this.medicationRequestsLoadingSubject.next(false)),
          catchError(() => {
            this.medicationRequestsLoadingSubject.next(false);
            return of([] as MedicationRequest[]);
          }),
          ignoreElements()
        );

        return merge(stateFiltered$, load$);
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    return this.medicationRequestsForEncounterLive$;
  }

  /**
   * Lazy/live stream for AllergyIntolerances for a specific patient.
   */
  connectAllergyIntolerances$(subjectIdToPass: string, encounterIdToPass: string, sourceData?: Bundle<AllergyIntolerance> | AllergyIntolerance[] | null): Observable<AllergyIntolerance[]> {
    if (this.allergyIntolerancesLive$) return this.allergyIntolerancesLive$;

    const normalizedPatientIdFromArgs = this.normalizeIdFromReference('Patient', subjectIdToPass);

    const patientId$ = normalizedPatientIdFromArgs
      ? of(normalizedPatientIdFromArgs)
      : this.state.currentPatientId$.pipe(distinctUntilChanged());

    this.allergyIntolerancesLive$ = patientId$.pipe(
      distinctUntilChanged(),
      switchMap((patientId) => {
        if (!patientId) return of([] as AllergyIntolerance[]);

        const fromInput = this.extractAllergyIntolerances(sourceData);
        if (fromInput) {
          return of(fromInput); // No filtering needed for now, assuming input is relevant
        }

        const stateFiltered$ = this.state.PatientResources.allergyIntolerances.pipe(
          map((wrapped) => (wrapped ?? []).map((r) => r.actualResource).filter(Boolean) as AllergyIntolerance[]),
          // map((requests) => this.filterAllergyIntolerances(requests, patientId)) // Filter if needed
        );

        this.allergyIntolerancesLoadingSubject.next(true);
        const load$ = this.utility.threeTierQueryAndSyncToStateOnce$<AllergyIntolerance>({
          resourceType: 'AllergyIntolerance',
          scope: 'patient',
          patientId,
          patientScopeSearchParam: 'patient', // AllergyIntolerance uses 'patient' or 'patient' (check spec, usually patient)
          urlSearchObject: {
            patient: `Patient/${patientId}`,
          },
          querySearchObject: [
            { key: 'patient.reference', value: [`Patient/${patientId}`, `${this.backendUrl}/Patient/${patientId}`] }
          ],
          tier1FollowNextLinks: true,
          tier3TriggerWhenBelowExpectedMinOnTier1Failure: false,
        }).pipe(
          tap(() => this.allergyIntolerancesLoadingSubject.next(false)),
          catchError(() => {
            this.allergyIntolerancesLoadingSubject.next(false);
            return of([] as AllergyIntolerance[]);
          }),
          ignoreElements()
        );

        return merge(stateFiltered$, load$);
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    return this.allergyIntolerancesLive$;
  }

  /**
   * Lazy/live stream for Past Diagnosis (FHIR Condition) NOT limited to the current encounter.
   */
  connectPastDiagnosisConditions$(subjectIdToPass: string, encounterIdToPass: string, sourceData?: Bundle<Condition> | Condition[] | null): Observable<Condition[]> {
    if (this.pastDiagnosisLive$) return this.pastDiagnosisLive$;

    const normalizedPatientIdFromArgs = this.normalizeIdFromReference('Patient', subjectIdToPass);

    const patientId$ = normalizedPatientIdFromArgs
      ? of(normalizedPatientIdFromArgs)
      : this.state.currentPatientId$.pipe(distinctUntilChanged());

    this.pastDiagnosisLive$ = patientId$.pipe(
      distinctUntilChanged(),
      switchMap((patientId) => {
        if (!patientId) return of([] as Condition[]);

        const fromInput = this.extractConditions(sourceData);
        if (fromInput) {
          return of(this.filterPastDiagnosisConditions(fromInput, patientId));
        }

        const stateFiltered$ = this.state.PatientResources.condition.pipe(
          map((wrapped) => (wrapped ?? []).map((r) => r.actualResource).filter(Boolean) as Condition[]),
          map((conditions) => this.filterPastDiagnosisConditions(conditions, patientId))
        );

        this.pastDiagnosisLoadingSubject.next(true);
        const load$ = this.utility.threeTierQueryAndSyncToStateOnce$<Condition>({
          resourceType: 'Condition',
          scope: 'patient',
          patientId,
          patientScopeSearchParam: 'subject',
          urlSearchObject: {
            subject: `Patient/${patientId}`,
          } as any,
          querySearchObject: [
            { key: 'subject.reference', value: [`Patient/${patientId}`, `${this.backendUrl}/Patient/${patientId}`] },
          ],
          tier1FollowNextLinks: true,
          tier3TriggerWhenBelowExpectedMinOnTier1Failure: false,
        }).pipe(
          tap(() => this.pastDiagnosisLoadingSubject.next(false)),
          catchError(() => {
            this.pastDiagnosisLoadingSubject.next(false);
            return of([] as Condition[]);
          }),
          ignoreElements()
        );

        return merge(stateFiltered$, load$);
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    return this.pastDiagnosisLive$;
  }

  /**
   * Lazy/live stream for Immunization history.
   */
  connectImmunizationHistory$(subjectIdToPass: string, sourceData?: Bundle<Immunization> | Immunization[] | null): Observable<Immunization[]> {
    if (this.immunizationHistoryLive$) return this.immunizationHistoryLive$;

    const normalizedPatientIdFromArgs = this.normalizeIdFromReference('Patient', subjectIdToPass);

    const patientId$ = normalizedPatientIdFromArgs
      ? of(normalizedPatientIdFromArgs)
      : this.state.currentPatientId$.pipe(distinctUntilChanged());

    this.immunizationHistoryLive$ = patientId$.pipe(
      distinctUntilChanged(),
      switchMap((patientId) => {
        if (!patientId) return of([] as Immunization[]);

        const fromInput = this.extractImmunizations(sourceData);
        if (fromInput) {
          return of(this.filterImmunizationHistory(fromInput, patientId));
        }

        const stateFiltered$ = this.state.PatientResources.immunizations.pipe(
          map((wrapped) => (wrapped ?? []).map((r) => r.actualResource).filter(Boolean) as Immunization[]),
          tap((imms) => console.log('Immunization history (raw from state):', imms)),
          map((imms) => this.filterImmunizationHistory(imms, patientId)),
          tap((imms) => console.log('Immunization history (filtered):', imms))
        );

        this.immunizationHistoryLoadingSubject.next(true);
        const load$ = this.utility.threeTierQueryAndSyncToStateOnce$<Immunization>({
          resourceType: 'Immunization',
          scope: 'patient',
          patientId,
          patientScopeSearchParam: 'patient',
          urlSearchObject: {
            patient: `Patient/${patientId}`,
          } as any,
          querySearchObject: [
            { key: 'patient.reference', value: [`Patient/${patientId}`, `${this.backendUrl}/Patient/${patientId}`] },
          ],
          tier1FollowNextLinks: true,
          tier3TriggerWhenBelowExpectedMinOnTier1Failure: false,
        }).pipe(
          tap((res) => console.log('Immunization history (raw from API):', res)),
          tap(() => this.immunizationHistoryLoadingSubject.next(false)),
          catchError(() => {
            this.immunizationHistoryLoadingSubject.next(false);
            return of([] as Immunization[]);
          }),
          ignoreElements()
        );

        return merge(stateFiltered$, load$).pipe(
          tap((_imms) => {
// $#$            // /* alert('Immunization history (filtered) */:' + JSON.stringify(_imms));
          })
        );
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    return this.immunizationHistoryLive$;
  }

  private extractAllergyIntolerances(sourceData?: Bundle<AllergyIntolerance> | AllergyIntolerance[] | null): AllergyIntolerance[] | null {
    if (!sourceData) return null;
    if (Array.isArray(sourceData)) return sourceData as AllergyIntolerance[];

    const bundle = sourceData as Bundle<AllergyIntolerance>;
    const entries = bundle.entry ?? [];
    return entries
      .map((e) => e.resource as AllergyIntolerance)
      .filter(Boolean);
  }

  private extractMedicationRequests(sourceData?: Bundle<MedicationRequest> | MedicationRequest[] | null): MedicationRequest[] | null {
    if (!sourceData) return null;
    if (Array.isArray(sourceData)) return sourceData as MedicationRequest[];

    const bundle = sourceData as Bundle<MedicationRequest>;
    const entries = bundle.entry ?? [];
    return entries
      .map((e) => e.resource as MedicationRequest)
      .filter(Boolean);
  }

  private filterMedicationRequestsForEncounter(requests: MedicationRequest[], patientId: string, encounterId: string): MedicationRequest[] {
    return (requests ?? []).filter((mr) => {
      const matchesEncounter = this.referenceMatches(mr.encounter?.reference, 'Encounter', encounterId);
      if (!matchesEncounter) return false;

      const matchesSubject = this.referenceMatches(mr.subject?.reference, 'Patient', patientId);
      if (!matchesSubject) return false;

      return true;
    });
  }

  /**
   * Loads patient symptoms as FHIR R4 Observations.
   * Filters:
   * - Observation.category contains `social-history` OR `exam`
   * - Observation.performer references current patient
   *
   * If `sourceData` is provided (Bundle or Observation[]), it will be used instead of a network call.
   */
  loadPatientSymptoms(subjectIdToPass: string, encounterIdToPass: string, sourceData?: Bundle<Observation> | Observation[] | null): Observable<Observation[]> {
    // Back-compat: one-shot read (still lazy-loads through the same pipeline).
    return this.connectPatientSymptoms$(subjectIdToPass, encounterIdToPass, sourceData).pipe(take(1));
  }

  private loadPatientObservationResources(): Observable<{ referenceId: string | null; savedStatus: 'saved' | 'unsaved'; actualResource: Observation; }[]> {
    return this.state.PatientResources.observations;
  }
  private extractObservations(sourceData?: Bundle<Observation> | Observation[] | null): Observation[] | null {
    if (!sourceData) return null;

    if (Array.isArray(sourceData)) {
      return sourceData as Observation[];
    }

    // Bundle
    const bundle = sourceData as Bundle<Observation>;
    const entries = bundle.entry ?? [];
    return entries
      .map((e) => e.resource as Observation)
      .filter(Boolean);
  }

  private filterSymptomObservations(observations: Observation[], patientId: string, encounterId: string | null): Observation[] {
    console.log('Filtering symptom observations for patient', patientId, 'from total observations', observations.length);
    const expectedPatientRefs = new Set([`Patient/${patientId}`, `${this.backendUrl}/Patient/${patientId}`, patientId]);

    return (observations ?? []).filter((obs) => {
      const categoryCodes = (obs.category ?? []).flatMap((cc) => (cc.coding ?? []).map((c) => c.code).filter(Boolean) as string[]);
      const isSocialHistoryOrExam = categoryCodes.includes('social-history') || categoryCodes.includes('exam');
      if (!isSocialHistoryOrExam) return false;

      //status or final
      if (obs.status !== 'final' && obs.status !== 'corrected') return false;
      
      // Scoped to the current encounter so symptoms don't bleed across historical visits
      if (encounterId) {
        const matchesEncounter = this.referenceMatches(obs?.encounter?.reference, 'Encounter', encounterId);
        if (!matchesEncounter) return false;
      }

      // Requirement: "also, patient as performer"
      const performerRefs = (obs.performer ?? [])
        .map((r) => (r.reference ?? '').trim())
        .filter(Boolean) as string[];
      const hasPatientPerformer = performerRefs.some((ref) => expectedPatientRefs.has(ref) || this.normalizeIdFromReference('Patient', ref) === patientId);
      return hasPatientPerformer;
    });
  }

  private filterVitalObservations(observations: Observation[], patientId: string, encounterId: string): Observation[] {
    const patientRef = `Patient/${patientId}`;
    const encounterRef = `Encounter/${encounterId}`;

    return (observations ?? []).filter((obs) => {
      const matchesEncounter = this.referenceMatches(obs?.encounter?.reference, 'Encounter', encounterId);
      if (!matchesEncounter) return false;

      const matchesSubject = this.referenceMatches(obs?.subject?.reference, 'Patient', patientId) || (obs?.subject?.reference ?? '') === patientRef;
      if (!matchesSubject) return false;

      const isVital = (obs.category ?? []).some((cat) => {
        const codes = (cat.coding ?? []).map((c) => c.code);
        if (codes.includes('vital-signs')) return true;
        return this.utility.searchCodeableConceptByDisplayOrText(cat as any, 'vital signs');
      });


      return isVital;
    });
  }

  private filterPhysicalExamObservations(observations: Observation[], patientId: string, encounterId: string): Observation[] {
    return (observations ?? []).filter((obs) => {
      const matchesEncounter = this.referenceMatches(obs?.encounter?.reference, 'Encounter', encounterId);
      if (!matchesEncounter) return false;

      const matchesSubject = this.referenceMatches(obs?.subject?.reference, 'Patient', patientId);
      if (!matchesSubject) return false;

      const isExam = (obs.category ?? []).some((cat) => {
        const codes = (cat.coding ?? []).map((c) => c.code);
        if (codes.includes('exam')) return true;
        return this.utility.searchCodeableConceptByDisplayOrText(cat as any, 'exam');
      });
      if (!isExam) return false;

      //either final or corrected status
      if (obs.status !== 'final' && obs.status !== 'corrected') return false;


      // Match existing component behavior: ignore patient-performed findings in physical exam section.
      const hasPatientPerformer = (obs.performer ?? []).some((p) => (p.reference ?? '').toLowerCase().includes('patient/'));
      return !hasPatientPerformer;
    });
  }

  private extractServiceRequests(sourceData?: Bundle<ServiceRequest> | ServiceRequest[] | null): ServiceRequest[] | null {
    if (!sourceData) return null;
    if (Array.isArray(sourceData)) return sourceData as ServiceRequest[];

    const bundle = sourceData as Bundle<ServiceRequest>;
    const entries = bundle.entry ?? [];
    return entries
      .map((e) => e.resource as ServiceRequest)
      .filter(Boolean);
  }

  private extractDiagnosticReports(sourceData?: Bundle<DiagnosticReport> | DiagnosticReport[] | null): DiagnosticReport[] | null {
    if (!sourceData) return null;
    if (Array.isArray(sourceData)) return sourceData as DiagnosticReport[];

    const bundle = sourceData as Bundle<DiagnosticReport>;
    const entries = bundle.entry ?? [];
    return entries
      .map((e) => e.resource as DiagnosticReport)
      .filter(Boolean);
  }

  private extractConditions(sourceData?: Bundle<Condition> | Condition[] | null): Condition[] | null {
    if (!sourceData) return null;
    if (Array.isArray(sourceData)) return sourceData as Condition[];

    const bundle = sourceData as Bundle<Condition>;
    const entries = bundle.entry ?? [];
    return entries
      .map((e) => e.resource as Condition)
      .filter(Boolean);
  }

  private filterDiagnosisConditionsForEncounter(conditions: Condition[], patientId: string, encounterId: string): Condition[] {
    return (conditions ?? []).filter((c) => {
// $#$      // /* alert(JSON.stringify(c) */)
      const matchesEncounter = this.referenceMatches((c as any)?.encounter?.reference, 'Encounter', encounterId);
      if (!matchesEncounter) return false;

      const matchesSubject = this.referenceMatches((c as any)?.subject?.reference, 'Patient', patientId);
      if (!matchesSubject) return false;

      // either provisional, confirmed, unconfirmed, or differential
      const isRelevantStatus = ['provisional', 'confirmed', 'unconfirmed', 'differential'].includes((c?.verificationStatus as any)?.coding?.[0]?.code ?? '');
      if (!isRelevantStatus) return false;

      return true;
    });
  }

  private filterPastDiagnosisConditions(conditions: Condition[], patientId: string): Condition[] {
    return (conditions ?? []).filter((c) => {
      const matchesSubject = this.referenceMatches((c as any)?.subject?.reference, 'Patient', patientId);
      if (!matchesSubject) return false;

      // for history, we might want even more statuses like 'resolved'
      const isRelevantStatus = ['provisional', 'confirmed', 'differential', 'resolved'].includes((c?.verificationStatus as any)?.coding?.[0]?.code ?? '');
      // if not found in verificationStatus, maybe clinicalStatus
      const isRelevantClinicalStatus = ['active', 'recurrence', 'relapse', 'resolved'].includes((c?.clinicalStatus as any)?.coding?.[0]?.code ?? '');

      return isRelevantStatus || isRelevantClinicalStatus;
    });
  }

  private filterImmunizationHistory(imms: Immunization[], patientId: string): Immunization[] {
// $#$    // /* alert("i am here") */
    return (imms ?? []).filter((imm) => {
      const matchesPatient = this.referenceMatches(imm.patient?.reference, 'Patient', patientId);
      if (!matchesPatient) return false;

      return imm.status === 'completed';
    });
  }

  private extractImmunizations(sourceData?: Bundle<Immunization> | Immunization[] | null): Immunization[] | null {
    if (!sourceData) return null;
    if (Array.isArray(sourceData)) return sourceData as Immunization[];

    const bundle = sourceData as Bundle<Immunization>;
    const entries = bundle.entry ?? [];
    return entries
      .map((e) => e.resource as Immunization)
      .filter(Boolean);
  }

  private filterFinalDiagnosticReportsForEncounter(reports: DiagnosticReport[], patientId: string, encounterId: string): DiagnosticReport[] {
    return (reports ?? []).filter((dr) => {
      const isFinal = (dr?.status ?? '').toString().toLowerCase() === 'final';
      if (!isFinal) return false;

      const matchesSubject = this.referenceMatches(dr?.subject?.reference, 'Patient', patientId);
      if (!matchesSubject) return false;

      const matchesEncounter = this.referenceMatches((dr as any)?.encounter?.reference, 'Encounter', encounterId);
      if (!matchesEncounter) return false;

      return true;
    });
  }

  private filterServiceRequestsForEncounter(requests: ServiceRequest[], patientId: string, encounterId: string): ServiceRequest[] {
    return (requests ?? []).filter((sr) => {
      const matchesEncounter = this.referenceMatches((sr as any)?.encounter?.reference, 'Encounter', encounterId);
      if (!matchesEncounter) return false;

      const matchesSubject = this.referenceMatches(sr?.subject?.reference, 'Patient', patientId);
      if (!matchesSubject) return false;

      return true;
    });
  }

  private pickFinalDiagnosticReportForServiceRequest(reports: DiagnosticReport[], serviceRequestId: string): DiagnosticReport | null {
    const srId = (serviceRequestId ?? '').toString().trim();
    if (!srId) return null;

    const isForServiceRequest = (dr: DiagnosticReport): boolean => {
      const refs = (dr?.basedOn ?? []) as any[];
      return refs.some((r) => this.referenceMatches((r?.reference ?? '').toString(), 'ServiceRequest' as any, srId) || (r?.reference ?? '').toString().includes(`ServiceRequest/${srId}`));
    };

    const finals = (reports ?? []).filter((dr) => (dr?.status ?? '').toString().toLowerCase() === 'final' && isForServiceRequest(dr));
    if (finals.length === 0) return null;

    // choose the most recent by effectiveDateTime/issued/meta.lastUpdated
    const getWhen = (dr: any): number => new Date(dr?.effectiveDateTime || dr?.issued || dr?.meta?.lastUpdated || 0).getTime();
    finals.sort((a, b) => getWhen(b) - getWhen(a));
    return finals[0] ?? null;
  }

  private normalizeIdFromReference(resourceType: string, value: string | null | undefined): string {
    const input = (value ?? '').trim();
    if (!input) return '';
    const withoutQuery = input.split('?')[0];
    const parts = withoutQuery.split('/').filter(Boolean);
    if (parts.length === 0) return '';
    const last = parts[parts.length - 1];
    // If the caller passed "Patient/{id}" or "Encounter/{id}", return id.
    if (parts.length >= 2) {
      const prev = parts[parts.length - 2];
      if (prev.toLowerCase() === resourceType.toLowerCase()) return last;
    }
    // If they passed raw id, accept it.
    return last;
  }

  private referenceMatches(reference: string | null | undefined, resourceType: string, id: string): boolean {
    const ref = (reference ?? '').trim();
    if (!ref || !id) return false;
    const canonical = `${resourceType}/${id}`;
    return ref === canonical || ref.endsWith(`/${canonical}`) || this.normalizeIdFromReference(resourceType, ref) === id;
  }
}
